import { Pane, Heading } from 'evergreen-ui';
import { useState, useEffect } from 'react';
import Calendar from './Calendar';
import AppointmentDialog from './AppointmentDialog';
import ViewEntryDialog from './ViewEntryDialog';
import DescriptionDialog from './DescriptionDialog';
import EditDescriptionDialog from './EditDescriptionDialog';
import EditNoteDialog from './EditNoteDialog';
import useDiary from './Diary/useDiary';
import ShiftBlocks from './Diary/ShiftBlocks';
import DiaryTimeGrid from './Diary/DiaryTimeGrid';

const Diary = ({ guests = [], diaryEntries: propEntries, setDiaryEntries, isDirector, isEducator }) => {
  const [selectedShiftPeriod, setSelectedShiftPeriod] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const toggleExpand = (id) => setExpandedEntries(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const {
    entries, futureAppointments, selectedDate, setSelectedDate,
    userId, containerRef, isMobile,
    editingEntry, setEditingEntry,
    editingNote, setEditingNote,
    showAddDialog, setShowAddDialog,
    showViewDialog, setShowViewDialog,
    showDescriptionDialog, setShowDescriptionDialog,
    showEditDescriptionDialog, setShowEditDescriptionDialog,
    showEditNoteDialog, setShowEditNoteDialog,
    fetchEntries, onEventClick, onEmptyDoubleClick,
    handleAddSubmit, handleDelete,
  } = useDiary({ guests, diaryEntries: propEntries, setDiaryEntries, isDirector, isEducator });

  useEffect(() => { setExpandedEntries(new Set()); }, [selectedDate]);

  const refresh = async (force = false) => {
    const fresh = await fetchEntries(selectedDate, force);
    if (setDiaryEntries) setDiaryEntries(fresh || []);
  };

  return (
    <Pane>
      <Heading size={600} marginBottom={16}>Diario</Heading>

      <Pane display={isMobile ? 'block' : 'flex'} gap={16} marginBottom={16}>
        <div style={{ minWidth: isMobile ? '100%' : 360 }}>
          <Calendar selected={selectedDate} setSelected={setSelectedDate} appointments={futureAppointments} />
        </div>

        <Pane flex={1} display="flex" flexDirection="column" gap={12}>
          <div className="diary-list">
            <ShiftBlocks
              entries={entries}
              userId={userId}
              isDirector={isDirector}
              isMobile={isMobile}
              expandedEntries={expandedEntries}
              toggleExpand={toggleExpand}
              onOpenAdd={(periodKey) => { setSelectedShiftPeriod(periodKey); setShowDescriptionDialog(true); }}
              onOpenEdit={(entry) => { setEditingEntry(entry); setShowEditDescriptionDialog(true); }}
            />
            <DiaryTimeGrid
              entries={entries}
              selectedDate={selectedDate}
              containerRef={containerRef}
              onEmptyDoubleClick={onEmptyDoubleClick}
              onEventClick={onEventClick}
            />
          </div>
        </Pane>
      </Pane>

      <AppointmentDialog
        isShown={showAddDialog}
        onClose={() => { setShowAddDialog(false); setEditingEntry(null); }}
        onSave={(payload) => handleAddSubmit(payload)}
        onDelete={(id) => handleDelete(id)}
        entry={editingEntry}
        guests={guests}
        canEdit={isDirector || isEducator}
        userId={userId}
        selectedDate={selectedDate}
      />

      <DescriptionDialog
        isShown={showDescriptionDialog}
        onClose={() => { setShowDescriptionDialog(false); setEditingEntry(null); setSelectedShiftPeriod(null); }}
        onSave={() => refresh(true)}
        userId={userId}
        selectedDate={selectedDate}
        shiftPeriod={selectedShiftPeriod}
      />

      <ViewEntryDialog
        isShown={showViewDialog}
        onClose={() => { setShowViewDialog(false); setEditingEntry(null); }}
        entry={editingEntry}
      />

      <EditDescriptionDialog
        isShown={showEditDescriptionDialog}
        onClose={() => { setShowEditDescriptionDialog(false); setEditingEntry(null); }}
        descriptionEntry={editingEntry}
        currentUserIsDirector={isDirector}
        onSaved={() => refresh(true)}
      />

      <EditNoteDialog
        isShown={showEditNoteDialog}
        onClose={() => { setShowEditNoteDialog(false); setEditingNote(null); setEditingEntry(null); }}
        note={editingNote ? (editingNote.data || editingNote) : null}
        guests={guests}
        currentUserIsDirector={isDirector}
        onSaved={() => refresh(true)}
      />
    </Pane>
  );
};

export default Diary;
